import { WorkspaceMark } from "src/features/workspaceMark/WorkspaceMark";
import "@repo/cs-chat/styles.css";

import { ApolloProvider } from "@apollo/client/react";
import { Toaster } from "@repo/ui4/components/Toaster";
import { useWorkspaceIdFromRouter } from "@repo/workspace-route";
import type { AppProps } from "next/app";
import Head from "next/head";
import { useRouter } from "next/router";
import { AuthorizedGuard } from "src/components/AuthorizedGuard";
import { LoadingBox } from "src/components/LoadingBox";
import { NotFoundScreen } from "src/components/NotFoundScreen";
import { AuthProvider } from "src/features/auth/AuthProvider";
import { ShopAdminCsChatUnreadProvider } from "src/features/csChat/ShopAdminCsChatUnreadProvider";
import { FeatureFlagsProvider } from "src/features/featureFlags/provider";
import { useApollo } from "src/hooks/useApollo";
import { pagesPath } from "src/utils/$path";

import "../lib/initDayjs";

const APP_TITLE = "Speria アカウント管理";

const App = ({ Component, pageProps }: AppProps) => {
  const router = useRouter();
  const isWorkspacePath = router.pathname.startsWith("/w/[workspaceId]");
  const { isReady, workspaceId } = useWorkspaceIdFromRouter();
  const routeWorkspaceId = isWorkspacePath ? workspaceId : null;

  return (
    <>
      <Head>
        <title>{APP_TITLE}</title>
        <meta name="robots" content="noindex" />
      </Head>
      <div className="min-h-screen bg-background font-sans text-foreground">
        {isWorkspacePath && !isReady ? (
          <LoadingBox />
        ) : isWorkspacePath && workspaceId == null ? (
          <NotFoundScreen />
        ) : (
          <AppInternal
            key={routeWorkspaceId}
            Component={Component}
            pageProps={pageProps}
            workspaceId={routeWorkspaceId}
          />
        )}
      </div>
    </>
  );
};

const publicPaths: string[] = [
  "/_healthz",
  pagesPath.$404.$url().pathname,
  pagesPath.debug.auth.$url().pathname,
  pagesPath.oauth.zoom.callback.$url().pathname,
  pagesPath.oauth.torqee.callback.$url().pathname,
];
const legacyPaths: string[] = [
  pagesPath.$url().pathname,
  pagesPath._legacyPath([]).$url().pathname,
];

const AppInternal = ({
  Component,
  pageProps,
  workspaceId,
}: {
  Component: AppProps["Component"];
  pageProps: AppProps["pageProps"];
  workspaceId: string | null;
}) => {
  const router = useRouter();
  const client = useApollo(workspaceId);
  const isPublicPath = publicPaths.includes(router.pathname);
  const isLegacyPath = legacyPaths.includes(router.pathname);

  return (
    <ApolloProvider client={client}>
      <FeatureFlagsProvider>
        <AuthProvider>
          <Toaster />
          {isPublicPath ? (
            <Component {...pageProps} />
          ) : (
            <AuthorizedGuard
              workspaceId={workspaceId}
              AuthenticatedWrapper={
                isLegacyPath ? undefined : ShopAdminCsChatUnreadProvider
              }
            >
              <Component {...pageProps} />
              <WorkspaceMark appTitle={APP_TITLE} />
            </AuthorizedGuard>
          )}
        </AuthProvider>
      </FeatureFlagsProvider>
    </ApolloProvider>
  );
};

export default App;
